import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  expertsApi,
  type ExpertsPreviewConsultant,
  type ExpertsGenerateResult,
  type ExpertsDraftKind,
} from '../services/api';
import { Spinner } from '../components/shared/Spinner';
import { useToast } from '../context/ToastContext';

/**
 * Auto-facturation Experts — Phase 2 + 3.
 *
 * Une carte par draft : un consultant peut avoir 1 ou 2 drafts (sessions
 * pour clients externes + sessions internes KESTIO). Chaque draft a ses
 * propres invoice_number / PU / lignes. La TVA est partagée entre les
 * drafts d'un même consultant et persistée en DB.
 */

// ─── Edits locaux ────────────────────────────────────────────────────────
interface LineEdit {
  hours: number;
  unit_price: number;
}
interface DraftEdit {
  invoice_number: string;
  unit_price: number; // PU "par défaut" appliqué aux lignes non overridées
  lines: Record<string, LineEdit>; // clé = client_id
}
interface ConsultantEdit {
  vat_rate: number;
  drafts: Partial<Record<ExpertsDraftKind, DraftEdit>>;
}
type EditsState = Record<string, ConsultantEdit>;

const VAT_OPTIONS = [
  { value: 0, label: '0 % (franchise)' },
  { value: 5.5, label: '5,5 %' },
  { value: 10, label: '10 %' },
  { value: 20, label: '20 %' },
];

const KIND_LABELS: Record<ExpertsDraftKind, string> = {
  external: 'Clients externes',
  internal_kestio: 'Sessions KESTIO (interne)',
};

const KIND_BADGE: Record<ExpertsDraftKind, string> = {
  external: 'bg-blue-100 text-blue-800 ring-blue-200',
  internal_kestio: 'bg-purple-100 text-purple-800 ring-purple-200',
};

function buildInitialEdits(consultants: ExpertsPreviewConsultant[]): EditsState {
  const out: EditsState = {};
  for (const c of consultants) {
    const drafts: Partial<Record<ExpertsDraftKind, DraftEdit>> = {};
    for (const d of c.drafts) {
      const pu = d.default_unit_price ?? 0;
      drafts[d.kind] = {
        invoice_number: d.suggested_next_number ?? '',
        unit_price: pu,
        lines: Object.fromEntries(
          d.lines.map((l) => [l.client_id, { hours: l.hours, unit_price: pu }]),
        ),
      };
    }
    out[c.consultant_id] = {
      vat_rate: c.default_vat_rate ?? 0,
      drafts,
    };
  }
  return out;
}

function defaultMonth(): string {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y!, mo!, 1); // mo (1-based) → mois suivant
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ─── Persistance locale (localStorage) ─────────────────────────────────────
// Le mois sélectionné et les éditions (PU/heures/n°) sont mémorisés par mois
// pour survivre au changement d'onglet et au rechargement (les modifs ne sont
// plus perdues). Clé édits par mois pour ne pas mélanger les périodes.
const MONTH_KEY = 'experts.month';
const editsKey = (m: string) => `experts.edits.${m}`;

function loadMonth(): string {
  try {
    const m = localStorage.getItem(MONTH_KEY);
    if (m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m)) return m;
  } catch {
    /* localStorage indispo : on retombe sur le défaut */
  }
  return defaultMonth();
}

function loadSavedEdits(m: string): EditsState | null {
  try {
    const s = localStorage.getItem(editsKey(m));
    return s ? (JSON.parse(s) as EditsState) : null;
  } catch {
    return null;
  }
}

/**
 * Fusionne les défauts (calculés depuis la preview = last-bill PU, n° suggéré…)
 * avec les éditions sauvegardées : on ne reprend du localStorage que les clés
 * qui existent encore dans la preview courante (anti-stale), le reste = défaut.
 */
function mergeEdits(defaults: EditsState, saved: EditsState | null): EditsState {
  if (!saved) return defaults;
  const out: EditsState = {};
  for (const [cid, def] of Object.entries(defaults)) {
    const sv = saved[cid];
    if (!sv) {
      out[cid] = def;
      continue;
    }
    const drafts: ConsultantEdit['drafts'] = {};
    for (const [kind, dDef] of Object.entries(def.drafts) as [ExpertsDraftKind, DraftEdit][]) {
      const dSv = sv.drafts?.[kind];
      if (!dSv) {
        drafts[kind] = dDef;
        continue;
      }
      const lines: Record<string, LineEdit> = {};
      for (const [clientId, lDef] of Object.entries(dDef.lines)) {
        const lSv = dSv.lines?.[clientId];
        lines[clientId] = lSv ? { hours: lSv.hours, unit_price: lSv.unit_price } : lDef;
      }
      drafts[kind] = {
        invoice_number: dSv.invoice_number ?? dDef.invoice_number,
        unit_price: dSv.unit_price ?? dDef.unit_price,
        lines,
      };
    }
    out[cid] = { vat_rate: sv.vat_rate ?? def.vat_rate, drafts };
  }
  return out;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function resultKey(consultantId: string, kind: ExpertsDraftKind): string {
  return `${consultantId}::${kind}`;
}

export function ExpertsAutoFacturationPage() {
  const [month, setMonthState] = useState<string>(loadMonth);
  const toast = useToast();
  const qc = useQueryClient();

  // Setter du mois qui persiste le choix (plus de reset au changement d'onglet).
  const setMonth = (m: string) => {
    setMonthState(m);
    try {
      if (m) localStorage.setItem(MONTH_KEY, m);
    } catch {
      /* ignore */
    }
  };

  const q = useQuery({
    queryKey: ['experts', 'preview', month],
    queryFn: () => expertsApi.preview(month),
    // Pas de staleTime/refetchInterval : les sync KLive → CRM se font ~1×/sem
    // manuellement par Valérie. Le rafraîchissement est donc déclenché
    // explicitement via le bouton « Recharger », pas en background.
    staleTime: Infinity,
  });

  // Indicateur de fraîcheur : on recalcule sur un timer 30 s pour refléter
  // l'écoulement sans re-render à chaque seconde.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(i);
  }, []);
  const freshness = useMemo(() => {
    if (!q.dataUpdatedAt) return null;
    const diffMin = Math.round((now - q.dataUpdatedAt) / 60_000);
    if (diffMin <= 0) return "à l'instant";
    if (diffMin === 1) return 'il y a 1 min';
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const hours = Math.round(diffMin / 60);
    return `il y a ${hours} h`;
  }, [now, q.dataUpdatedAt]);

  const [edits, setEdits] = useState<EditsState>({});
  const [results, setResults] = useState<Record<string, ExpertsGenerateResult>>({});
  // Mois auquel `edits` correspond — évite de persister des édits sous le
  // mauvais mois pendant le bref instant entre changement de mois et refetch.
  const editsMonthRef = useRef<string | null>(null);
  useEffect(() => {
    if (q.data?.consultants) {
      const defaults = buildInitialEdits(q.data.consultants);
      setEdits(mergeEdits(defaults, loadSavedEdits(month)));
      editsMonthRef.current = month;
      setResults({});
    }
  }, [q.data, month]);

  // Persiste les édits du mois courant (PU, heures, n°, TVA) à chaque modif.
  useEffect(() => {
    if (editsMonthRef.current === month && Object.keys(edits).length > 0) {
      try {
        localStorage.setItem(editsKey(month), JSON.stringify(edits));
      } catch {
        /* quota/indispo : non bloquant */
      }
    }
  }, [edits, month]);

  // ─── Helpers d'édition ────────────────────────────────────────────────
  const setVatRate = (consultantId: string, v: number) =>
    setEdits((prev) => ({
      ...prev,
      [consultantId]: { ...prev[consultantId]!, vat_rate: v },
    }));
  const setDraftInvoiceNumber = (consultantId: string, kind: ExpertsDraftKind, v: string) =>
    setEdits((prev) => {
      const c = prev[consultantId];
      if (!c) return prev;
      const d = c.drafts[kind];
      if (!d) return prev;
      return {
        ...prev,
        [consultantId]: { ...c, drafts: { ...c.drafts, [kind]: { ...d, invoice_number: v } } },
      };
    });
  const setDraftUnitPrice = (consultantId: string, kind: ExpertsDraftKind, v: number) =>
    setEdits((prev) => {
      const c = prev[consultantId];
      if (!c) return prev;
      const d = c.drafts[kind];
      if (!d) return prev;
      const prevPu = d.unit_price;
      const newLines: Record<string, LineEdit> = {};
      for (const [k, le] of Object.entries(d.lines)) {
        newLines[k] = le.unit_price === prevPu ? { ...le, unit_price: v } : le;
      }
      return {
        ...prev,
        [consultantId]: {
          ...c,
          drafts: { ...c.drafts, [kind]: { ...d, unit_price: v, lines: newLines } },
        },
      };
    });
  const setLineHours = (
    consultantId: string,
    kind: ExpertsDraftKind,
    clientId: string,
    v: number,
  ) =>
    setEdits((prev) => {
      const c = prev[consultantId];
      if (!c) return prev;
      const d = c.drafts[kind];
      if (!d) return prev;
      return {
        ...prev,
        [consultantId]: {
          ...c,
          drafts: {
            ...c.drafts,
            [kind]: { ...d, lines: { ...d.lines, [clientId]: { ...d.lines[clientId]!, hours: v } } },
          },
        },
      };
    });
  const setLinePu = (
    consultantId: string,
    kind: ExpertsDraftKind,
    clientId: string,
    v: number,
  ) =>
    setEdits((prev) => {
      const c = prev[consultantId];
      if (!c) return prev;
      const d = c.drafts[kind];
      if (!d) return prev;
      return {
        ...prev,
        [consultantId]: {
          ...c,
          drafts: {
            ...c.drafts,
            [kind]: {
              ...d,
              lines: { ...d.lines, [clientId]: { ...d.lines[clientId]!, unit_price: v } },
            },
          },
        },
      };
    });

  // ─── Drafts "prêts" à générer ─────────────────────────────────────────
  // (consultant_id + kind) qui ont un vendor mappé, un n° non vide, et des heures > 0.
  const readyDraftKeys = useMemo(() => {
    if (!q.data) return new Set<string>();
    const out = new Set<string>();
    for (const c of q.data.consultants) {
      if (!c.vendor_books_id) continue;
      const e = edits[c.consultant_id];
      if (!e) continue;
      for (const d of c.drafts) {
        const de = e.drafts[d.kind];
        if (!de) continue;
        if (!de.invoice_number.trim()) continue;
        const totalHours = d.lines.reduce((s, l) => s + (de.lines[l.client_id]?.hours ?? 0), 0);
        if (totalHours <= 0) continue;
        out.add(resultKey(c.consultant_id, d.kind));
      }
    }
    return out;
  }, [q.data, edits]);

  // ─── Résumé global ────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!q.data) return null;
    let totalAmount = 0;
    let totalHours = 0;
    let draftCount = 0;
    let warningsCount = 0;
    for (const c of q.data.consultants) {
      const e = edits[c.consultant_id];
      for (const d of c.drafts) {
        draftCount++;
        warningsCount += d.warnings.length;
        const de = e?.drafts[d.kind];
        for (const l of d.lines) {
          const le = de?.lines[l.client_id];
          const h = le?.hours ?? l.hours;
          const pu = le?.unit_price ?? de?.unit_price ?? d.default_unit_price ?? 0;
          totalHours += h;
          totalAmount += round2(h * pu);
        }
      }
    }
    return {
      consultants: q.data.consultants.length,
      drafts: draftCount,
      hours: round2(totalHours),
      amount: round2(totalAmount),
      warnings: warningsCount,
    };
  }, [q.data, edits]);

  // ─── Mutation ─────────────────────────────────────────────────────────
  // Accepte un filtre : si scope est défini, on n'envoie QUE ces drafts
  // (utile pour le bouton "Générer cette facture" par carte). Sinon, on
  // envoie tous les drafts prêts (bouton global du bas).
  const generateMutation = useMutation({
    mutationFn: (scope?: Set<string>) => {
      if (!q.data) throw new Error('Pas de données');
      const target = scope ?? readyDraftKeys;
      const payload = {
        month,
        consultants: q.data.consultants
          .map((c) => {
            const e = edits[c.consultant_id]!;
            const drafts = c.drafts
              .filter((d) => target.has(resultKey(c.consultant_id, d.kind)))
              .map((d) => {
                const de = e.drafts[d.kind]!;
                return {
                  kind: d.kind,
                  invoice_number: de.invoice_number.trim(),
                  lines: d.lines.map((l) => ({
                    client_id: l.client_id,
                    client_name: l.client_name,
                    hours: de.lines[l.client_id]?.hours ?? 0,
                    unit_price: de.lines[l.client_id]?.unit_price ?? de.unit_price,
                    session_ids: l.session_ids,
                  })),
                };
              });
            if (drafts.length === 0) return null;
            return {
              consultant_id: c.consultant_id,
              consultant_name: c.consultant_name,
              vendor_books_id: c.vendor_books_id!,
              vat_rate: e.vat_rate || null,
              drafts,
            };
          })
          .filter((c): c is NonNullable<typeof c> => c !== null),
      };
      return expertsApi.generate(payload);
    },
    onSuccess: (resp, scope) => {
      setResults((prev) => {
        // Merge : conserve les résultats précédents (les autres drafts) et
        // remplace ceux qui viennent d'être (re)générés.
        const next = { ...prev };
        for (const r of resp.results) next[resultKey(r.consultant_id, r.kind)] = r;
        return next;
      });
      const success = resp.results.filter((r) => r.status === 'success').length;
      const partial = resp.results.filter((r) => r.status === 'partial').length;
      const failed = resp.results.filter((r) => r.status === 'failed').length;
      if (failed === 0 && partial === 0) {
        toast.success(`${success} facture(s) générée(s) avec succès`);
      } else {
        toast.info(`${success} OK · ${partial} partielles · ${failed} en échec`);
      }
      qc.invalidateQueries({ queryKey: ['experts', 'preview'] });

      // Finalisation : si c'était la génération GLOBALE (scope indéfini) et que
      // tout est passé, on bascule par défaut sur le mois suivant (la période
      // est traitée). Le mois reste modifiable pour revenir en arrière.
      if (scope === undefined && resp.results.length > 0 && failed === 0 && partial === 0) {
        const nm = nextMonth(month);
        setMonth(nm);
        toast.info(`Mois ${month} finalisé → passage à ${nm}`);
      }
    },
    onError: (err) => toast.error(`Génération échouée : ${String(err)}`),
  });

  // Quelle clé est actuellement en cours de génération (pour spinners ciblés).
  const [pendingScope, setPendingScope] = useState<Set<string> | null>(null);
  const runGenerate = (scope?: Set<string>) => {
    setPendingScope(scope ?? readyDraftKeys);
    generateMutation.mutate(scope, {
      onSettled: () => setPendingScope(null),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Auto-facturation Experts</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sessions Conseil « A facturer » du mois sélectionné. Les sessions pour KESTIO
            (interne) sont séparées en une facture distincte.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {freshness && (
            <span className="text-xs text-slate-400" title={new Date(q.dataUpdatedAt).toLocaleString('fr-FR')}>
              Chargé {freshness}
            </span>
          )}
          <button
            type="button"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="px-2.5 py-1.5 rounded-md text-sm text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5"
            title="Re-requêter Zoho CRM pour récupérer les sessions ajoutées depuis"
          >
            {q.isFetching ? <Spinner /> : <span aria-hidden>↻</span>}
            Recharger
          </button>
          <label className="text-sm text-slate-600 ml-2">Mois</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-slate-500">Chargement de la preview… (~5–10 s)</p>
      ) : q.isError ? (
        <p className="text-red-600">Erreur : {String(q.error)}</p>
      ) : !q.data || q.data.consultants.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
          Aucune session Conseil « A facturer » pour {month}.
        </div>
      ) : (
        <>
          {summary && (
            <div className="bg-white rounded-lg border border-slate-200 px-4 py-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <span><strong>{summary.consultants}</strong> consultant(s)</span>
              <span><strong>{summary.drafts}</strong> facture(s) à générer</span>
              <span><strong>{summary.hours.toFixed(2)}</strong> h totales</span>
              <span className="font-medium">Total HT : {summary.amount.toFixed(2)} €</span>
              {summary.warnings > 0 && (
                <span className="text-amber-700">⚠️ {summary.warnings} alerte(s)</span>
              )}
            </div>
          )}

          <div className="space-y-4">
            {q.data.consultants.map((c) => {
              const e = edits[c.consultant_id];
              if (!e) return null;
              return (
                <article
                  key={c.consultant_id}
                  className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                >
                  {/* Header consultant */}
                  <header className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="font-semibold text-slate-900">{c.consultant_name}</h2>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Vendor Books :{' '}
                        {c.vendor_books_name ? (
                          <span className="font-mono">{c.vendor_books_name}</span>
                        ) : (
                          <span className="text-red-600">non mappé</span>
                        )}
                        {c.last_bill_number && (
                          <> · Dernier n° : <span className="font-mono">{c.last_bill_number}</span></>
                        )}
                        {c.last_bill_date && <> ({c.last_bill_date})</>}
                      </p>
                    </div>
                    {/* TVA partagée entre les drafts du consultant */}
                    <label className="flex items-center gap-2 text-sm">
                      <span className="text-slate-600">TVA</span>
                      <select
                        value={e.vat_rate}
                        onChange={(ev) => setVatRate(c.consultant_id, Number(ev.target.value))}
                        className="rounded-md border border-slate-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {VAT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {c.default_vat_rate != null && c.default_vat_rate !== e.vat_rate && (
                        <span className="text-xs text-slate-400 italic">
                          (mémorisé : {c.default_vat_rate}%)
                        </span>
                      )}
                    </label>
                  </header>

                  {/* Une sous-section par draft */}
                  <div className="divide-y divide-slate-200">
                    {c.drafts.map((d) => {
                      const de = e.drafts[d.kind];
                      if (!de) return null;
                      const key = resultKey(c.consultant_id, d.kind);
                      const result = results[key];
                      const isReady = readyDraftKeys.has(key);
                      const isPending = pendingScope?.has(key) ?? false;
                      const draftTotal = d.lines.reduce((sum, l) => {
                        const le = de.lines[l.client_id];
                        const h = le?.hours ?? l.hours;
                        const pu = le?.unit_price ?? de.unit_price;
                        return sum + round2(h * pu);
                      }, 0);
                      // Une fois la bill créée (success OU partial), on bloque le
                      // bouton pour éviter qu'elle soit cliquée une 2e fois et
                      // génère un doublon non voulu (Books le bloquerait via
                      // l'idempotence, mais visuellement c'est ambigu).
                      const isDone = result?.status === 'success' || result?.status === 'partial';
                      return (
                        <section key={d.kind}>
                          {/* Bandeau kind */}
                          <div className="px-4 py-2 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${KIND_BADGE[d.kind]}`}
                            >
                              {KIND_LABELS[d.kind]}
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-sm tabular-nums">
                                Total HT : <strong>{round2(draftTotal).toFixed(2)} €</strong>
                              </span>
                              <button
                                type="button"
                                onClick={() => runGenerate(new Set([key]))}
                                disabled={!isReady || generateMutation.isPending || isDone}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${
                                  isDone
                                    ? 'bg-slate-200 text-slate-600 cursor-default'
                                    : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                }`}
                                title={
                                  isDone
                                    ? 'Bill déjà créée pour ce draft : refresh la page pour repartir d\'une preview à jour'
                                    : !isReady
                                      ? 'Vendor non mappé, n° vide ou heures = 0'
                                      : 'Génère uniquement cette facture'
                                }
                              >
                                {isPending && <Spinner />}
                                {isDone ? '✓ Facture générée' : isPending ? 'Génération…' : 'Générer cette facture'}
                              </button>
                            </div>
                          </div>

                          {/* Champs facture */}
                          <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <label className="flex flex-col gap-1">
                              <span className="text-slate-600">N° de facture</span>
                              <input
                                type="text"
                                value={de.invoice_number}
                                onChange={(ev) =>
                                  setDraftInvoiceNumber(c.consultant_id, d.kind, ev.target.value)
                                }
                                placeholder="ex. KESTIO-2026-05-001"
                                className="rounded-md border border-slate-300 px-2.5 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-slate-600">
                                PU par défaut (€/h){' '}
                                {d.default_unit_price == null && (
                                  <span className="text-amber-700">(pas de PU historique)</span>
                                )}
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                value={de.unit_price}
                                onChange={(ev) =>
                                  setDraftUnitPrice(c.consultant_id, d.kind, Number(ev.target.value))
                                }
                                className="rounded-md border border-slate-300 px-2.5 py-1.5 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </label>
                          </div>

                          {/* Warnings */}
                          {d.warnings.length > 0 && (
                            <div className="px-4 py-2 bg-amber-50 border-y border-amber-200 text-xs text-amber-800">
                              {d.warnings.map((w, i) => (
                                <div key={i}>⚠️ {w}</div>
                              ))}
                            </div>
                          )}

                          {/* Statut génération */}
                          {result && <ResultBadge result={result} />}

                          {/* Lignes */}
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
                              <tr>
                                <th className="px-4 py-2 font-medium">Client</th>
                                <th className="px-3 py-2 font-medium text-right w-28">Heures</th>
                                <th className="px-3 py-2 font-medium text-right w-28">PU (€/h)</th>
                                <th className="px-3 py-2 font-medium text-right w-32">Sous-total HT</th>
                                <th className="px-3 py-2 font-medium text-right w-20">Sessions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {d.lines.map((l) => {
                                const le = de.lines[l.client_id]!;
                                const sub = round2(le.hours * le.unit_price);
                                return (
                                  <tr key={l.client_id}>
                                    <td
                                      className="px-4 py-2 font-medium truncate max-w-[260px]"
                                      title={l.client_name}
                                    >
                                      {l.client_name}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={le.hours}
                                        onChange={(ev) =>
                                          setLineHours(
                                            c.consultant_id,
                                            d.kind,
                                            l.client_id,
                                            Number(ev.target.value),
                                          )
                                        }
                                        className="w-24 rounded-md border border-slate-300 px-2 py-1 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={le.unit_price}
                                        onChange={(ev) =>
                                          setLinePu(
                                            c.consultant_id,
                                            d.kind,
                                            l.client_id,
                                            Number(ev.target.value),
                                          )
                                        }
                                        className="w-24 rounded-md border border-slate-300 px-2 py-1 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                                      {sub.toFixed(2)} €
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs text-slate-500 tabular-nums">
                                      {l.session_ids.length}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </section>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              {readyDraftKeys.size} facture(s) prêtes à générer. Bouton ci-dessous
              pour TOUT générer en lot ; ou bouton par carte pour piloter une à la fois.
            </p>
            <button
              type="button"
              onClick={() => runGenerate()}
              disabled={generateMutation.isPending || readyDraftKeys.size === 0}
              className="px-4 py-2 rounded-md font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {generateMutation.isPending && pendingScope?.size === readyDraftKeys.size && <Spinner />}
              {generateMutation.isPending && pendingScope?.size === readyDraftKeys.size
                ? `Génération en cours (${readyDraftKeys.size})…`
                : `Générer tout (${readyDraftKeys.size})`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ResultBadge({ result }: { result: ExpertsGenerateResult }) {
  const colors: Record<ExpertsGenerateResult['status'], string> = {
    success: 'bg-emerald-50 ring-emerald-200 text-emerald-800',
    partial: 'bg-amber-50 ring-amber-200 text-amber-800',
    failed: 'bg-red-50 ring-red-200 text-red-800',
    skipped: 'bg-slate-50 ring-slate-200 text-slate-700',
  };
  const labels: Record<ExpertsGenerateResult['status'], string> = {
    success: '✓ Bill créée + archivée + CRM mis à jour',
    partial: '⚠️ Bill créée mais étapes annexes en erreur',
    failed: '✕ Échec',
    skipped: 'Skippée',
  };
  return (
    <div
      className={`px-4 py-2 ring-1 ring-inset text-xs border-y border-slate-200 ${colors[result.status]}`}
    >
      <div className="font-medium">{labels[result.status]}</div>
      {result.bill_number && (
        <div className="mt-0.5">
          Bill : <span className="font-mono">{result.bill_number}</span>
          {result.bill_already_existed && <span className="ml-2 italic">(existait déjà)</span>}
          {' · '}
          <span>{result.crm_sessions_updated} session(s) CRM mises à jour</span>
        </div>
      )}
      {(result.dropbox_expert_path || result.dropbox_accountant_path) && (
        <div className="mt-0.5 font-mono text-[10px] opacity-80 break-all space-y-0.5">
          {result.dropbox_expert_path && <div>📁 {result.dropbox_expert_path}</div>}
          {result.dropbox_accountant_path && <div>📁 {result.dropbox_accountant_path}</div>}
        </div>
      )}
      {result.errors.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {result.errors.map((e, i) => (
            <div key={i}>⚠️ {e}</div>
          ))}
        </div>
      )}
    </div>
  );
}
